import {
  Component,
  OnInit,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { SolutionSpace } from '../../models/solution';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ResponseHandlerService } from 'src/app/services/responseHandler.service';
import { Project } from 'src/app/models/project';
import { PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { Title } from '@angular/platform-browser';
import { SortDirection, ProjectsSortOrder } from 'src/app/models/page-request';
import { TranslatePipe } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ZxProjectListCardComponent } from '../zx-project-list-card/zx-project-list-card.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { SearchFieldComponent } from '../../shared/search-field/search-field.component';
import { MatTabsModule } from '@angular/material/tabs';

import { ButtonComponent } from '../../shared/button/button.component';
import { InfoPageComponent } from '../../shared/info-page/info-page.component';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { LoadMoreDirective } from '../../shared/load-more.directive';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';

@Component({
  selector: 'app-zx-projects-overview',
  templateUrl: './zx-projects-overview.component.html',
  styleUrls: ['./zx-projects-overview.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    InfoPageComponent,
    ButtonComponent,
    RouterLink,
    MatTabsModule,
    SearchFieldComponent,
    MatButtonModule,
    MatMenuModule,
    AngularSvgIconModule,
    ZxProjectListCardComponent,
    MatPaginatorModule,
    LoadMoreDirective,
    MatProgressSpinnerModule,
    TranslatePipe,
  ],
})
export class ZxProjectsOverviewComponent implements OnInit {
  activeLink = 'utilization';
  solutionSpace: SolutionSpace;
  loaded = false;
  /** Phones have no paginator: the next page is appended when the end of the list scrolls into view. */
  readonly bp = inject(BreakpointService);
  loadingMore = false;
  /** The last request for the next page failed: the list shows a retry button instead of loading on its own. */
  loadError = false;
  private requestId = 0;
  private wasPhone = this.bp.isPhone();
  private paramSubscription: Subscription;

  type: string[] | undefined;
  archived = false;
  sortBy: ProjectsSortOrder = ProjectsSortOrder.PROJECT_NAME;
  sortDirection: SortDirection = SortDirection.ASC;
  pageNumber = 1;
  pageSize = 9;
  totalItems = 0;
  displayedText = 'Active';
  links = [
    { id: 'utilization', label: 'Utilization', path: '../utilization' },
    { id: 'humidity', label: 'Humidity', path: '../humidity' },
  ];
  search: string | undefined = undefined;

  private orgSubscription: Subscription;

  constructor(
    private projectsService: ProjectsService,
    private route: ActivatedRoute,
    private responseHandler: ResponseHandlerService,
    private titleService: Title,
    private authService: AuthenticationService
  ) {
    // Phones grow one list while wider screens page through it: switching between the two starts again at page 1.
    effect(() => {
      const phone = this.bp.isPhone();
      untracked(() => {
        if (phone === this.wasPhone) return;
        this.wasPhone = phone;
        if (!this.solutionSpace) return;
        this.pageNumber = 1;
        this.loadProjects(1, this.pageSize, this.sortDirection, this.sortBy, this.search, this.archived);
      });
    });
  }

  canAccess: string[] = [];
  projects: Project[] = [];

  ngOnInit(): void {
    this.titleService.setTitle('Projects');

    this.orgSubscription = this.authService.getUserAccessPermissions().subscribe((canAccess) => {
      this.canAccess = canAccess;
    });

    this.paramSubscription = this.route.params.subscribe((el) => {
      if (el.utilorhumid === 'humidity') {
        this.activeLink = 'humidity';
        this.solutionSpace = SolutionSpace.Humidity;
      } else {
        this.activeLink = 'utilization';
        this.solutionSpace = SolutionSpace.Utilization;
      }
      this.loadProjects(this.pageNumber, this.pageSize, this.sortDirection, this.sortBy, this.search, this.archived);
    });
  }

  uniqueComponentTypes(): { value: string; label: string }[] {
    const uniques: { value: string; label: string }[] = [];

    return uniques;
  }

  loadProjects(
    pageNumber: number,
    pageSize: number,
    sortDirection: SortDirection,
    sortOrder: ProjectsSortOrder,
    searchText: string | undefined,
    archived: boolean,
    append = false
  ): void {
    // A newer request (another search, filter or page) makes this answer stale.
    const requestId = ++this.requestId;
    if (append) {
      this.loadingMore = true;
      this.loadError = false;
    } else {
      this.loaded = false;
      this.loadingMore = false;
      this.loadError = false;
      if (this.bp.isPhone()) this.pageNumber = pageNumber = 1; // a new search or filter starts at the top
    }
    this.projectsService
      .getProjects({
        solutionSpace: this.solutionSpace,
        pageRequest: {
          pageNumber: pageNumber,
          pageSize: pageSize,
          sortDirection: sortDirection,
          sortOrder: sortOrder,
        },
        likeSearch: searchText,
        archived: archived,
      })
      .subscribe(
        (projects) => {
          if (requestId !== this.requestId) return;
          this.totalItems = projects.totalItemCount;
          this.projects = append ? [...this.projects, ...projects.items] : projects.items;
          // this.componentTypes = this.uniqueComponentTypes();
          this.loaded = true;
          this.loadingMore = false;
        },
        (error: HttpErrorResponse) => {
          if (requestId !== this.requestId) return;
          this.loadingMore = false;
          if (append) {
            // Back to the last page that did load, and wait for the user to retry that same next page.
            this.pageNumber--;
            this.loadError = true;
          }
          this.responseHandler.error(error);
        }
      );
  }
  getOrigin(): string {
    return document.location.pathname;
  }

  onSearchFieldUpdate(search?: string): void {
    this.search = search;
    this.loadProjects(this.pageNumber, this.pageSize, this.sortDirection, this.sortBy, this.search, this.archived);
  }

  dualSolutions(): boolean {
    return this.canAccess.includes('utilization') && this.canAccess.includes('humidity');
  }

  setTypeControl(typeControl: UntypedFormControl): void {
    // eslint-disable-next-line
    this.type = typeControl.value ? (typeControl.value as string[]) : undefined;
    this.loadProjects(this.pageNumber, this.pageSize, this.sortDirection, this.sortBy, this.search, this.archived);
  }

  setArchiveControl(archived: boolean): void {
    if (archived) {
      this.displayedText = 'Archived';
    } else {
      this.displayedText = 'Active';
    }
    // eslint-disable-next-line
    this.archived = archived;
    this.loadProjects(this.pageNumber, this.pageSize, this.sortDirection, this.sortBy, this.search, this.archived);
  }

  setSortControl(sortControl: UntypedFormControl): void {
    // eslint-disable-next-line
    const tempSortBy = typeof sortControl.value == 'string' ? sortControl.value.toLocaleLowerCase() : undefined;
    this.sortBy = tempSortBy === 'name' ? ProjectsSortOrder.PROJECT_NAME : ProjectsSortOrder.CREATED;
    this.loadProjects(this.pageNumber, this.pageSize, this.sortDirection, this.sortBy, this.search, this.archived);
  }

  setSortDirectionControl(sortDirectionControl: UntypedFormControl): void {
    const tempSortDirection =
      typeof sortDirectionControl.value == 'string' ? sortDirectionControl.value.toLocaleLowerCase() : undefined;
    this.sortDirection = tempSortDirection == '0' ? SortDirection.ASC : SortDirection.DESC;
    this.loadProjects(this.pageNumber, this.pageSize, this.sortDirection, this.sortBy, this.search, this.archived);
  }

  /** Phones: fetch the next page and add it to the list. */
  loadNextPage(): void {
    if (!this.loaded || this.loadingMore || this.projects.length >= this.totalItems) return;
    this.loadError = false;
    this.pageNumber++;
    this.loadProjects(
      this.pageNumber,
      this.pageSize,
      this.sortDirection,
      this.sortBy,
      this.search,
      this.archived,
      true
    );
  }

  // pagination event
  handlePageEvent(event: PageEvent): void {
    if (event.pageIndex !== undefined) {
      this.pageNumber = event.pageIndex + 1;
    }
    this.loadProjects(this.pageNumber, this.pageSize, this.sortDirection, this.sortBy, this.search, this.archived);
  }
}
